// src/components/chat/ThinkingSection.tsx

import * as React from "react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import MarkdownRenderer from "../../helpers/MarkdownRenderer";
import { useLearningTip } from "../../hooks/useLearningTip";
import { useAgent } from "../../contexts/AgentContext";
import TipsIcon from "./TipsIcon";

/**
 * Props for the inline thinking segment.
 *
 * One segment per `thinking_start` → `thinking_stop` pair. The streaming hook
 * positions these inline in the message at the character offset where they
 * began, so multi-step tool loops render thinking → text → thinking → text in
 * natural order.
 */
interface ThinkingSectionProps {
  /** Current thinking content. Streams in live while `isStreaming` is true. */
  content: string;
  /** Whether this segment is still being produced by the model. */
  isStreaming: boolean;
  className?: string;
}

/**
 * Rotation intervals for the streaming header.
 * - Words rotate faster (3.3s) - short, quick variety
 * - Tips rotate slower (5.5s) - longer, need more reading time
 */
const WORD_ROTATION_MS = 3300;
const TIP_ROTATION_MS = 5500;

/**
 * Rotating thinking words shown while the model is producing the segment.
 * Organized alphabetically for easy maintenance.
 */
const THINKING_WORDS = [
  // A-C
  "Accomplishing...",
  "Actioning...",
  "Actualizing...",
  "Baking...",
  "Booping...",
  "Brewing...",
  "Calculating...",
  "Cerebrating...",
  "Channelling...",
  "Churning...",
  "Coalescing...",
  "Cogitating...",
  "Combobulating...",
  "Computing...",
  "Conjuring...",
  "Considering...",
  "Contemplating...",
  "Contracting...",
  "Cooking...",
  "Crafting...",
  "Creating...",
  "Crunching...",
  // D-F
  "Deciphering...",
  "Deliberating...",
  "Determining...",
  "Discombobulating...",
  "Divining...",
  "Doing...",
  "Effecting...",
  "Elucidating...",
  "Enchanting...",
  "Envisioning...",
  "Finagling...",
  "Forging...",
  "Forming...",
  // G-I
  "Generating...",
  "Germinating...",
  "Hatching...",
  "Herding...",
  "Hustling...",
  "Ideating...",
  "Imagining...",
  "Incubating...",
  "Inferring...",
  // J-M
  "Jiving...",
  "Manifesting...",
  "Marinating...",
  "Meandering...",
  "Mulling...",
  "Mustering...",
  "Musing...",
  // N-P
  "Noodling...",
  "Percolating...",
  "Perusing...",
  "Philosophising...",
  "Pondering...",
  "Pontificating...",
  "Processing...",
  "Puttering...",
  "Puzzling...",
  // R-S
  "Reticulating...",
  "Ruminating...",
  "Schlepping...",
  "Shimmying...",
  "Simmering...",
  "Smooshing...",
  "Spelunking...",
  "Spinning...",
  "Sussing...",
  "Synthesizing...",
  // T-Z
  "Thinking...",
  "Tinkering...",
  "Unfurling...",
  "Unravelling...",
  "Vibing...",
  "Wandering...",
  "Whirring...",
  "Wizarding...",
  "Working...",
  "Wrangling...",
] as const;

/**
 * Citation markup the model may echo into its reasoning: <doc-cite …/> tags and
 * <<t1-c2>> / <<d1-c2>> ref markers. No citation payloads are sent for thinking,
 * and rehype-raw would parse a bare marker as an unknown element, so both are
 * hidden at render time only - the stored thinking is paired with its signature.
 */
const DOC_CITE_TAG = /<\/?doc-cite\b[^>]*>/gi;
const CITATION_REF_MARKER = /<<[td][1-9]\d*-c[1-9]\d*>>/g;

const stripCitationMarkup = (content: string): string =>
  content.replace(DOC_CITE_TAG, "").replace(CITATION_REF_MARKER, "");

const ThinkingSection: React.FC<ThinkingSectionProps> = ({
  content,
  isStreaming,
  className = "",
}) => {
  // Collapsed by default so the answer stays front and center; users who want
  // the reasoning expand it manually. The animated ellipsis on the header is
  // the "still working" cue while collapsed.
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [currentMessage, setCurrentMessage] = useState<string>(THINKING_WORDS[0]);
  const [isShowingTip, setIsShowingTip] = useState(false);

  const rotationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastWordIndex = useRef<number>(-1);

  const { currentAgent } = useAgent();
  const { shouldShowTip, getNextTip } = useLearningTip({
    agentId: currentAgent?.id || "ccm-space",
    isThinking: isStreaming,
  });

  // Refs avoid stale closures inside setTimeout callbacks.
  const shouldShowTipRef = useRef(shouldShowTip);
  const getNextTipRef = useRef(getNextTip);
  shouldShowTipRef.current = shouldShowTip;
  getNextTipRef.current = getNextTip;

  const pickRandomWord = useCallback((): string => {
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * THINKING_WORDS.length);
    } while (nextIndex === lastWordIndex.current && THINKING_WORDS.length > 1);
    lastWordIndex.current = nextIndex;
    return THINKING_WORDS[nextIndex];
  }, []);

  const performRotation = useCallback(() => {
    const showTip = shouldShowTipRef.current();
    if (showTip) {
      const tipContent = getNextTipRef.current();
      if (tipContent) {
        setCurrentMessage(tipContent);
        setIsShowingTip(true);
        rotationTimeoutRef.current = setTimeout(performRotation, TIP_ROTATION_MS);
        return;
      }
    }
    const word = pickRandomWord();
    setCurrentMessage(word);
    setIsShowingTip(false);
    rotationTimeoutRef.current = setTimeout(performRotation, WORD_ROTATION_MS);
  }, [pickRandomWord]);

  // Start/stop the header rotation when streaming starts/stops.
  useEffect(() => {
    if (isStreaming) {
      const showTip = shouldShowTipRef.current();
      if (showTip) {
        const tipContent = getNextTipRef.current();
        if (tipContent) {
          setCurrentMessage(tipContent);
          setIsShowingTip(true);
          rotationTimeoutRef.current = setTimeout(performRotation, TIP_ROTATION_MS);
        } else {
          const word = pickRandomWord();
          setCurrentMessage(word);
          setIsShowingTip(false);
          rotationTimeoutRef.current = setTimeout(performRotation, WORD_ROTATION_MS);
        }
      } else {
        const word = pickRandomWord();
        setCurrentMessage(word);
        setIsShowingTip(false);
        rotationTimeoutRef.current = setTimeout(performRotation, WORD_ROTATION_MS);
      }
    } else if (rotationTimeoutRef.current) {
      clearTimeout(rotationTimeoutRef.current);
      rotationTimeoutRef.current = null;
    }

    return () => {
      if (rotationTimeoutRef.current) {
        clearTimeout(rotationTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const headerMessage = isStreaming ? currentMessage : "Thought process";
  // While streaming, the trailing dots of the rotating word/tip are replaced
  // by the animated ellipsis, so strip them from the text itself.
  const streamingMessage = currentMessage.replace(/\.+$/, "");
  const showTipIcon = isStreaming && isShowingTip;
  const hasExpandableContent = content.length > 0;
  const isOpen = isExpanded && hasExpandableContent;
  const displayContent = useMemo(() => stripCitationMarkup(content), [content]);

  const headerStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#F8F9FA",
    borderRadius: isOpen ? "8px 8px 0 0" : "8px",
    border: "1px solid #E9ECEF",
    cursor: hasExpandableContent ? "pointer" : "default",
    transition: "background-color 0.2s ease",
    userSelect: "none",
    fontFamily: "'Segoe UI', sans-serif",
    textAlign: "left",
  };

  return (
    <div className={className} style={{ marginBottom: "12px" }}>
      {isStreaming && (
        <style>
          {`
            @keyframes thinking-ellipsis-bounce {
              0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
              30% { transform: translateY(-3px); opacity: 1; }
            }
            .thinking-ellipsis-dot {
              display: block;
              width: 4px;
              height: 4px;
              border-radius: 50%;
              background: #495057;
              animation: thinking-ellipsis-bounce 1.2s infinite ease-in-out;
            }
            .thinking-ellipsis-dot:nth-child(2) { animation-delay: 0.15s; }
            .thinking-ellipsis-dot:nth-child(3) { animation-delay: 0.3s; }
          `}
        </style>
      )}
      <button
        type="button"
        onClick={hasExpandableContent ? handleToggleExpanded : undefined}
        disabled={!hasExpandableContent}
        aria-expanded={hasExpandableContent ? isOpen : undefined}
        aria-label={`${isOpen ? "Collapse" : "Expand"} thought process`}
        style={headerStyle}
        onMouseEnter={(e) => {
          if (hasExpandableContent) {
            (e.currentTarget as HTMLElement).style.backgroundColor = "#E9ECEF";
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = "#F8F9FA";
        }}
      >
        {hasExpandableContent && (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              flexShrink: 0,
            }}
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="#6C757D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}

        <div
          style={{
            flex: 1,
            fontSize: "13px",
            color: "#495057",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {showTipIcon && <TipsIcon size={16} color="#0066cc" />}
          {isStreaming ? (
            <span style={{ display: "inline-flex", alignItems: "baseline" }}>
              <span>{showTipIcon ? `Tip: ${streamingMessage}` : streamingMessage}</span>
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "flex-end",
                  gap: "3px",
                  marginLeft: "4px",
                  // Headroom for the upward bounce so the dots never clip.
                  paddingTop: "4px",
                }}
              >
                <span className="thinking-ellipsis-dot" />
                <span className="thinking-ellipsis-dot" />
                <span className="thinking-ellipsis-dot" />
              </span>
            </span>
          ) : (
            headerMessage
          )}
        </div>
      </button>

      {hasExpandableContent && (
        <div
          aria-hidden={!isOpen}
          // `inert` keeps focus and AT out of the collapsed panel; cast because
          // React's CSSProperties doesn't yet model the HTML attribute.
          {...({ inert: !isOpen ? "" : undefined } as Record<string, string | undefined>)}
          style={{
            backgroundColor: "#FFFFFF",
            borderTop: "none",
            borderRight: isOpen ? "1px solid #E9ECEF" : "none",
            borderBottom: isOpen ? "1px solid #E9ECEF" : "none",
            borderLeft: isOpen ? "1px solid #E9ECEF" : "none",
            borderRadius: isOpen ? "0 0 8px 8px" : "0",
            maxHeight: isOpen ? "400px" : "0",
            overflow: "hidden",
            transition: "max-height 0.3s ease",
          }}
        >
          <div
            style={{
              boxSizing: "border-box",
              padding: "16px",
              maxHeight: "400px",
              overflowY: "auto",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "#212529",
            }}
          >
            <MarkdownRenderer>{displayContent}</MarkdownRenderer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThinkingSection;
